import { Button, ButtonLoading, Tooltip } from '@tovala/component-library'
import {
  State,
  useEditUserShipping,
  UserV1,
  UserV1ShippingAddress,
  useValidateShippingAddress,
  ValidateShippingAddressResponse,
} from '@tovala/browser-apis-combinedapi'
import {
  Control,
  Controller,
  FieldErrors,
  FormState,
  useForm,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form'
import { useState } from 'react'

import {
  ErrorCodeMessageMapCombinedAPI,
  ReactSelectValue,
} from 'types/internal'
import { formatPhone } from 'utils/general'
import { STATE_OPTIONS } from 'utils/states'
import { wrapWithContactSupportTeam } from 'utils/errors'

import AddressAutocomplete from 'components/common/AddressAutocomplete'
import APIErrorDisplay from 'components/common/APIErrorDisplay'
import FormComboboxRHF from 'components/common/FormComboboxRHF'
import FormInputRHF from 'components/common/FormInputRHF'

import Modal, { ModalHeader, ModalPanel } from 'components/common/Modal'
import { events } from 'analytics/events'
import { track } from 'utils/analytics'
import { isCombinedAPIResponseError } from 'utils/api'

interface AddressData {
  city: string
  line1: string
  line2: string
  name: string
  phone: string
  state: string
  zipCode: string
}

interface FormData {
  city: string
  fullName: string
  line1: string
  line2: string
  phone: string
  state: ReactSelectValue<string> | null
  zipCode: string
}

interface FormDataValidated {
  city: string
  fullName: string
  line1: string
  line2: string
  phone: string
  state: ReactSelectValue<string>
  zipCode: string
}

type AddressErrorCode = ValidateShippingAddressResponse['dpv_match_code']

const EDIT_SHIPPING_ERRORS: ErrorCodeMessageMapCombinedAPI = {
  Fallback: {
    helpToFix: 'Please try saving again.',
    why: "We couldn't save your shipping information due to a technical issue on our end.",
  },
  InvalidStateSelection: {
    helpToFix: 'Please reselect your state and try again.',
    why: "We couldn't save your shipping address because we didn't recognize the selected state.",
  },
  InvalidZipCode: {
    helpToFix: 'Please enter your five-digit zip code and try again.',
    why: "We couldn't save your shipping address because we didn't recognize the zip code.",
  },
  PhoneNumberInvalid: {
    helpToFix: 'Please double-check your phone number and try saving again.',
    wayOut: wrapWithContactSupportTeam('If you need further assistance'),
    why: "We couldn't save your shipping information because your phone number was not recognized as valid.",
  },
  POBoxAddressNotValid: {
    helpToFix: 'Please enter a non-P.O. box address.',
    wayOut: wrapWithContactSupportTeam('If you need further assistance'),
    why: "We couldn't save your shipping information because we don't currently deliver to P.O. Boxes.",
  },
}

const ShippingAddressForm = ({
  customerID,
  onEditUserSuccess,
  user,
}: {
  customerID: string
  onEditUserSuccess?(): void
  user: UserV1
}) => {
  const [addressErrorModalCode, setAddressErrorModalCode] =
    useState<AddressErrorCode | null>(null)

  const shippingAddress =
    user.shippingAddresses.length > 0 ? user.shippingAddresses[0] : undefined

  const {
    control,
    formState: { errors, isValid, touchedFields },
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm<FormData>({
    defaultValues: getInitialFormData(shippingAddress),
    mode: 'onChange',
  })
  const values = watch()

  const {
    error: validateAddressError,
    isError: hasValidateAddressError,
    isPending: isValidatingAddress,
    mutateAsync: validateShippingAddress,
  } = useValidateShippingAddress({
    onError: (err) => {
      if (
        isCombinedAPIResponseError(err) &&
        err.response?.data.message === 'POBoxAddressNotValid'
      ) {
        track(events.SHIPPING_ERROR_NO_PO_BOXES)
      }
    },
    onSuccess: (data) => {
      if (data.dpv_match_code !== 'Y') {
        setAddressErrorModalCode(data.dpv_match_code)
      }
    },
  })

  const {
    error: editUserShippingError,
    isError: hasEditUserShippingError,
    isPending: isEditingUserShipping,
    mutateAsync: editUserShipping,
  } = useEditUserShipping({
    onSuccess: () => {
      if (onEditUserSuccess) {
        onEditUserSuccess()
      }
    },
  })

  const saveShippingAddress = async ({ data }: { data: AddressData }) => {
    return editUserShipping({
      customerID,
      data: getAPISaveData(data),
      ignoreValidation: true,
      userID: user.id,
    })
  }

  const isSavingShipping = isValidatingAddress || isEditingUserShipping
  const shippingError = hasValidateAddressError
    ? validateAddressError
    : hasEditUserShippingError
      ? editUserShippingError
      : null
  return (
    <form
      onSubmit={handleSubmit(async (formData) => {
        const formDataValidated = formData as FormDataValidated

        const response = await validateShippingAddress({
          customerID,
          data: getAPISaveData({
            ...formDataValidated,
            name: formDataValidated.fullName,
            state: formDataValidated.state.value,
          }),
          userID: user.id,
        })

        if (response.dpv_match_code !== 'Y') {
          return
        }

        return saveShippingAddress({
          data: {
            ...response.address,
            zipCode: response.address.postal_code,
          },
        })
      })}
    >
      <SaveShippingAddressForm
        addressErrorModalCode={addressErrorModalCode}
        control={control}
        errors={errors as FieldErrors<FormData>}
        onCloseAddressErrorModal={() => {
          setAddressErrorModalCode(null)
        }}
        onConfirmAddress={async (opts) => {
          setAddressErrorModalCode(null)

          await saveShippingAddress({
            ...opts,
            data: {
              ...opts.formData,
              name: opts.formData.fullName,
              state: opts.formData.state.value,
            },
          })
        }}
        register={register}
        setValue={setValue}
        touchedFields={touchedFields}
        values={values}
      />

      <div className="my-4">
        {shippingError && (
          <APIErrorDisplay
            error={shippingError}
            errorCodeMessageMap={EDIT_SHIPPING_ERRORS}
          />
        )}
      </div>
      <div className="mt-6">
        <ButtonLoading
          disabled={!isValid}
          isLoading={isSavingShipping}
          size="large"
          type="submit"
        >
          Save Shipping Information
        </ButtonLoading>
      </div>
    </form>
  )
}

export default ShippingAddressForm

const SaveShippingAddressForm = ({
  addressErrorModalCode,
  control,
  errors,
  onCloseAddressErrorModal,
  onConfirmAddress,
  register,
  setValue,
  touchedFields,
  values,
}: {
  addressErrorModalCode: AddressErrorCode | null
  control: Control<FormData>
  errors: FieldErrors<FormData>
  onCloseAddressErrorModal(): void
  onConfirmAddress(opts: { formData: FormDataValidated }): Promise<void>
  register: UseFormRegister<FormData>
  setValue: UseFormSetValue<FormData>
  touchedFields: FormState<FormData>['touchedFields']
  values: FormData
}) => {
  return (
    <>
      <div className="grid grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-1 md:gap-x-0">
        <FormInputRHF
          error={touchedFields.fullName ? errors?.fullName?.message : undefined}
          id="full-name"
          label="Full Name"
          labelFor="full-name"
          {...register('fullName', {
            required: 'Please enter a shipping name',
          })}
        />
        <div className="md:hidden" />
        <div className="col-span-2 md:col-span-1">
          <AddressInput control={control} setValue={setValue} />
        </div>
        <FormInputRHF
          id="line2"
          label="Unit, apartment or suite number (No P.O. Boxes)"
          labelFor="line2"
          {...register('line2')}
        />
        <div className="md:hidden" />
        <FormInputRHF
          error={touchedFields.city ? errors?.city?.message : undefined}
          id="city"
          label="City"
          labelFor="city"
          {...register('city', {
            required: 'Please enter a city',
          })}
        />
        <FormComboboxRHF
          control={control}
          id="state"
          inputAriaLabel="State"
          label="State"
          name="state"
          options={STATE_OPTIONS}
          rules={{ required: 'Please select a state' }}
        />
        <FormInputRHF
          error={touchedFields.zipCode ? errors?.zipCode?.message : undefined}
          id="zipCode"
          label="Zip Code"
          labelFor="zipCode"
          {...register('zipCode', {
            required: 'Please enter a zip code',
          })}
        />
        <Controller
          control={control}
          name="phone"
          render={({ field, fieldState: { error, isTouched } }) => (
            <FormInputRHF
              ref={field.ref}
              error={isTouched ? error?.message : undefined}
              id="phone"
              inputMode="tel"
              label="Phone Number"
              labelFor="phone"
              maxLength={12}
              name={field.name}
              onBlur={field.onBlur}
              onChange={(e) => field.onChange(formatPhone(e.target.value))}
              rightIcon={
                <Tooltip
                  trigger={
                    <div className="flex h-6 w-6 select-none items-center justify-center rounded-full bg-grey-3 text-k/14_120">
                      ?
                    </div>
                  }
                >
                  Phone number is required for home deliveries.
                </Tooltip>
              }
              type="tel"
              value={field.value ?? ''}
            />
          )}
          rules={{
            required: 'Please enter a phone number',
            validate: (value) =>
              value.replace(/[^\d]/g, '').length === 10 ||
              'Please enter a 10-digit phone number',
          }}
        />
      </div>

      {addressErrorModalCode !== null && (
        <AddressErrorModal
          errorCode={addressErrorModalCode}
          onClickUseEnteredAddress={async () => {
            await onConfirmAddress({
              // This assumes this modal is only shown after initial form validation has been done.
              formData: values as FormDataValidated,
            })
          }}
          onCloseModal={onCloseAddressErrorModal}
        />
      )}
    </>
  )
}

const AddressInput = ({
  control,
  setValue,
}: {
  control: Control<FormData>
  setValue: UseFormSetValue<FormData>
}) => {
  return (
    <>
      <Controller
        control={control}
        name="line1"
        render={({ field: { value }, fieldState: { error, isTouched } }) => (
          <AddressAutocomplete
            error={isTouched ? error?.message : undefined}
            label="Address"
            labelFor="line1"
            name="line1"
            onChange={(line1) => {
              setValue('line1', line1)
            }}
            onSelectSuggestion={(address) => {
              if (address) {
                setValue('city', address.city)
                setValue('line1', address.line1)
                setValue('line2', '')
                setValue(
                  'state',
                  STATE_OPTIONS.find(({ value }) => value === address.state) ??
                    null,
                )
                setValue('zipCode', address.zipCode)
              }
            }}
            value={value}
          />
        )}
        rules={{
          required: 'Please enter an address',
        }}
      />
    </>
  )
}

const AddressErrorModal = ({
  errorCode,
  onClickUseEnteredAddress,
  onCloseModal,
}: {
  errorCode: AddressErrorCode
  onClickUseEnteredAddress(): void
  onCloseModal(): void
}) => {
  let content = (
    <p className="text-grey-8">
      We{' '}
      <span className="text-black">
        could not find an exact match for your shipping address
      </span>
      . Please edit your address, or confirm it's correct.
    </p>
  )

  if (errorCode === 'D') {
    content = (
      <p className="text-grey-8">
        Looks like you{' '}
        <span className="text-black">
          did not provide an apartment/suite number
        </span>
        . Please update your address and try again.
      </p>
    )
  }

  return (
    <Modal onCloseModal={onCloseModal}>
      <ModalPanel>
        <ModalHeader onClickClose={onCloseModal}>
          Address Incomplete
        </ModalHeader>

        <div className="p-6">
          <div className="pb-12 pt-6 text-center">{content}</div>
          <div className="flex h-12 justify-between space-x-4">
            <div className="grow">
              <Button
                buttonStyle="stroke"
                onClick={onClickUseEnteredAddress}
                size="fluid"
              >
                Use Entered Address
              </Button>
            </div>
            <div className="grow">
              <Button onClick={onCloseModal} size="fluid">
                Edit Address
              </Button>
            </div>
          </div>
        </div>
      </ModalPanel>
    </Modal>
  )
}

export function getAPISaveData(data: AddressData) {
  return {
    address: {
      city: data.city,
      line1: data.line1,
      line2: data.line2,
      postal_code: data.zipCode,
      state: data.state,
    },
    name: data.name,
    phone: data.phone.replace(/[^\d]/gi, ''),
  }
}

function getInitialFormData(
  shipping: UserV1ShippingAddress | undefined,
): FormData {
  const stateAbbr = shipping?.state ?? null
  let state: ReactSelectValue<State> | null = null
  if (stateAbbr) {
    state = STATE_OPTIONS.find(({ value }) => value === stateAbbr) ?? null
  }

  return {
    city: shipping?.city ?? '',
    fullName: shipping?.userName ?? '',
    line1: shipping?.line1 ?? '',
    line2: shipping?.line2 ?? '',
    phone: formatPhone(shipping?.phone ?? ''),
    state,
    zipCode: shipping?.zipCode ?? '',
  }
}
